import type { HintVerdict } from '@recipes/contracts';
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  cn,
} from '@recipes/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useLiveQuery } from 'dexie-react-hooks';
import { ArrowDownUp, MapPin, Plus, ScanBarcode, Store as StoreIcon } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useNavigate } from 'react-router';
import { toast } from '@/components/feedback/toaster';
import { NutriScoreBadge } from '@/features/catalog/badges';
import { removeStore } from '@/features/catalog/catalog-queue';
import { syncCatalog } from '@/features/catalog/device-copy';
import { useCatalogBoot, useCatalogStores, usePendingCatalog } from '@/features/catalog/hooks';
import { DepartmentOrderDialog } from '@/features/catalog/department-order-dialog';
import { StoreDialog } from '@/features/catalog/store-dialog';
import { StoreStars, starsText } from '@/features/catalog/store-rating';
import { ConfirmDialog } from '@/features/shared/confirm-dialog';
import { FeaturePageHeader, PageContainer } from '@/features/shared/page-header';
import { LazyTastingMap } from '@/features/tasting/map/lazy-map';
import { api } from '@/lib/api';
import { describeError, toastError } from '@/lib/errors';
import { useDebouncedValue } from '@/lib/hooks/use-debounced-value';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import { offlineDb } from '@/lib/offline/db';
import { queryKeys } from '@/lib/query-keys';

const ALL = '__all__';

export function CatalogTabs() {
  const { t } = useTranslation();
  const tabs = [
    { to: '/catalog/ingredients', label: t('catalog.tabs.ingredients') },
    { to: '/catalog/products', label: t('catalog.tabs.products') },
    { to: '/catalog/stores', label: t('catalog.tabs.stores') },
    { to: '/catalog/cards', label: t('catalog.tabs.cards') },
  ];
  return (
    <nav aria-label={t('catalog.title')} className="mb-4 flex gap-1 border-b">
      {tabs.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          className={({ isActive }) =>
            cn(
              '-mb-px border-b-2 px-3 py-2 text-sm font-medium',
              isActive
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )
          }
        >
          {tab.label}
        </NavLink>
      ))}
    </nav>
  );
}

export function PendingNotice() {
  const { t } = useTranslation();
  const pending = usePendingCatalog();
  if (pending.length === 0) return null;
  const failed = pending.filter((p) => p.lastError);
  return (
    <Alert
      variant={failed.length ? 'warning' : 'info'}
      className="mb-3"
      data-testid="catalog-pending"
    >
      {t('catalog.pendingCount', { count: pending.length })}
      {failed.length > 0 && ` ${t('catalog.pendingFailed', { message: failed[0]!.lastError })}`}
    </Alert>
  );
}

// ---------------------------------------------------------------------------
// Products
// ---------------------------------------------------------------------------

export function CatalogProductsPage() {
  const { t } = useTranslation();
  const online = useOnlineStatus();
  const qId = useId();
  const [q, setQ] = useState('');
  const [verdict, setVerdict] = useState<HintVerdict | typeof ALL>(ALL);
  const debounced = useDebouncedValue(q.trim(), 250);
  useCatalogBoot();

  const list = useQuery({
    queryKey: queryKeys.catalog.products({ q: debounced, verdict }),
    queryFn: () =>
      api.products.list({
        q: debounced || undefined,
        verdict: verdict === ALL ? undefined : verdict,
        limit: 100,
      }),
    enabled: online,
  });
  const local = useLiveQuery(() => offlineDb.catalogProducts.toArray(), []);
  const offlineItems = useMemo(
    () =>
      (local ?? [])
        .map((r) => r.product)
        .filter(
          (p) =>
            !debounced ||
            `${p.name} ${p.brand ?? ''} ${p.barcode ?? ''}`
              .toLowerCase()
              .includes(debounced.toLowerCase()),
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [local, debounced],
  );
  const items = online ? (list.data?.items ?? []) : offlineItems;

  return (
    <PageContainer className="max-w-3xl">
      <FeaturePageHeader
        title={t('catalog.title')}
        description={t('catalog.description')}
        actions={
          <Button asChild>
            <Link to="/catalog/scan" data-testid="catalog-scan">
              <ScanBarcode aria-hidden /> {t('catalog.scan.title')}
            </Link>
          </Button>
        }
      />
      <CatalogTabs />
      <PendingNotice />
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <div className="min-w-0 flex-1">
          <Label htmlFor={qId}>{t('common.search')}</Label>
          <Input
            id={qId}
            className="mt-1"
            value={q}
            placeholder={t('catalog.products.searchPlaceholder')}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <Select value={verdict} onValueChange={(v) => setVerdict(v as HintVerdict | typeof ALL)}>
          <SelectTrigger className="w-44" aria-label={t('catalog.hint.verdict')} disabled={!online}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('catalog.products.allVerdicts')}</SelectItem>
            <SelectItem value="RECOMMENDED">{t('catalog.verdict.RECOMMENDED')}</SelectItem>
            <SelectItem value="AVOID">{t('catalog.verdict.AVOID')}</SelectItem>
            <SelectItem value="NOTE">{t('catalog.verdict.NOTE')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {online && list.isLoading ? (
        <Spinner label={t('app.loading')} />
      ) : online && list.error ? (
        <Alert variant="destructive">{describeError(list.error)}</Alert>
      ) : items.length === 0 ? (
        <EmptyState
          title={t('catalog.products.emptyTitle')}
          description={t('catalog.products.emptyBody')}
        />
      ) : (
        <ul className="divide-y rounded-lg border" data-testid="product-list">
          {items.map((product) => (
            <li key={product.id}>
              <Link
                to={`/catalog/products/${product.id}`}
                className="flex min-h-[56px] items-center gap-3 px-3 py-2 hover:bg-accent/40"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{product.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {[product.brand, product.ingredient?.name, product.barcode]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
                {product.nutriScore && <NutriScoreBadge grade={product.nutriScore} size="sm" />}
                {product.hintSummary.avoid > 0 && (
                  <Badge variant="destructive">{t('catalog.verdict.AVOID')}</Badge>
                )}
                {product.hintSummary.avoid === 0 && product.hintSummary.recommended > 0 && (
                  <Badge variant="success">{t('catalog.verdict.RECOMMENDED')}</Badge>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </PageContainer>
  );
}

// ---------------------------------------------------------------------------
// Stores
// ---------------------------------------------------------------------------

export function CatalogStoresPage() {
  const { t } = useTranslation();
  const online = useOnlineStatus();
  const navigate = useNavigate();
  const stores = useCatalogStores();
  const [addOpen, setAddOpen] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [keepHints, setKeepHints] = useState(true);
  const [ordering, setOrdering] = useState<string | null>(null);
  useCatalogBoot();

  const orderTarget = stores.find((s) => s.id === ordering) ?? null;
  const branches = stores.filter((s) => s.lat !== null && s.lng !== null);
  const remove = useMutation({
    mutationFn: (id: string) => removeStore(id, keepHints ? 'keep' : 'delete'),
    onSuccess: () => {
      toast.success(t('catalog.stores.deleted'));
      setDeleting(null);
      void syncCatalog();
    },
    onError: (e) => toastError(e),
  });
  const target = stores.find((s) => s.id === deleting) ?? null;

  return (
    <PageContainer className="max-w-3xl">
      <FeaturePageHeader
        title={t('catalog.title')}
        description={t('catalog.stores.description')}
        actions={
          <Button onClick={() => setAddOpen(true)} data-testid="store-add">
            <Plus aria-hidden /> {t('catalog.stores.add')}
          </Button>
        }
      />
      <CatalogTabs />
      <PendingNotice />
      {stores.length === 0 ? (
        <EmptyState
          icon={<StoreIcon aria-hidden />}
          title={t('catalog.stores.emptyTitle')}
          description={t('catalog.stores.emptyBody')}
        />
      ) : (
        <ul className="mb-4 divide-y rounded-lg border" data-testid="store-list">
          {stores.map((store) => (
            <li key={store.id} className="flex min-h-[52px] items-center gap-3 px-3 py-2">
              {store.kind === 'BRANCH' ? (
                <MapPin className="size-5 shrink-0 text-primary" aria-hidden />
              ) : (
                <StoreIcon className="size-5 shrink-0 text-primary" aria-hidden />
              )}
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <Link
                    to={`/catalog/stores/${store.id}`}
                    className="min-w-0 truncate font-medium underline-offset-2 hover:underline"
                    data-testid="store-link"
                  >
                    {store.name}
                  </Link>
                  <StoreStars rating={store.rating} className="shrink-0" />
                </span>
                <span className="block truncate text-xs text-muted-foreground">
                  {[
                    store.kind === 'CHAIN' ? t('catalog.stores.chain') : store.parentName,
                    store.street,
                    store.city,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  {(store.departmentOrder ?? []).length > 0 && ` · ${t('catalog.departments.own')}`}
                </span>
              </span>
              <Badge variant="muted">
                {t('catalog.stores.hintCount', { count: store.hintCount })}
              </Badge>
              {store.editable && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setOrdering(store.id)}
                  aria-label={`${t('catalog.departments.action')}: ${store.name}`}
                  title={t('catalog.departments.action')}
                  data-testid="store-department-order"
                >
                  <ArrowDownUp aria-hidden />
                </Button>
              )}
              {store.editable && (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={!online}
                  onClick={() => {
                    setKeepHints(true);
                    setDeleting(store.id);
                  }}
                >
                  {t('common.delete')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {branches.length > 0 && (
        <LazyTastingMap
          ariaLabel={t('catalog.stores.map')}
          points={branches.map((s) => ({
            id: s.id,
            lat: s.lat as number,
            lng: s.lng as number,
            label: s.name,
            detail: starsText(s.rating),
            own: true,
          }))}
          // Name and stars always at the pin; a click or tap opens the store (user, 2026-09-24).
          labels
          onSelect={(ids) => navigate(`/catalog/stores/${ids[0]}`)}
          fitToPoints
          className="h-72"
        />
      )}
      <StoreDialog open={addOpen} onOpenChange={setAddOpen} />
      {orderTarget && (
        <DepartmentOrderDialog
          store={orderTarget}
          stores={stores}
          open
          onOpenChange={(next) => !next && setOrdering(null)}
        />
      )}
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={t('catalog.stores.deleteTitle', { name: target?.name ?? '' })}
        confirmLabel={t('common.delete')}
        destructive
        loading={remove.isPending}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting);
        }}
      >
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={keepHints}
            onChange={(e) => setKeepHints(e.target.checked)}
          />
          {t('catalog.stores.keepHints')}
        </label>
      </ConfirmDialog>
    </PageContainer>
  );
}
